// src/components/WelcomeScreen.js
// ── First-run welcome screen ───────────────────────────────────────────────────
// Tour: loads full sample dataset → reloads → compact floating card
// Clears ALL sample data on finish including staff, targets, meetings, briefs

import { useState, useEffect } from "react";
import { supabase } from "../config/supabase";

const FONT  = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
const BG    = "#0A1720";
const SURF  = "#0F2230";
const SURF2 = "#142D3E";
const BORD  = "#1E3448";
const CYAN  = "#22D3EE";
const TEXT  = "#FFFFFF";
const SEC   = "#D1D5DB";
const MUTED = "#7A8894";

function ExIQMark({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" x="12" y="12" rx="7" fill={CYAN}/>
      <rect width="4"  height="48" x="34" y="12" fill="#061018"/>
      <rect width="48" height="4"  x="12" y="34" fill="#061018"/>
      <rect width="7"  height="7"  x="32.5" y="32.5" rx="1.5" fill={CYAN}/>
      <rect width="4"  height="11" x="34" y="1"  rx="2" fill={CYAN}/>
      <rect width="4"  height="11" x="34" y="60" rx="2" fill={CYAN}/>
      <rect width="11" height="4"  x="1"  y="34" rx="2" fill={CYAN}/>
      <rect width="11" height="4"  x="60" y="34" rx="2" fill={CYAN}/>
    </svg>
  );
}

// ── Sample data constants ──────────────────────────────────────────────────────
const SAMPLE_EVENTS_META = [
  { name: "AUSA Annual 2026",   start: "2026-10-13", end: "2026-10-15", location: "Washington, DC",     status: "Confirmed",   goal_leads: 120, goal_pipeline: 9000000, goal_meetings: 18, planned_budget: 55000, team: "Defense",       objective: "Generate qualified leads from Army program offices and prime contractors. Focus on C5ISR and logistics modernization programs." },
  { name: "WEST 2026",          start: "2026-02-17", end: "2026-02-19", location: "San Diego, CA",       status: "Complete",    goal_leads: 80,  goal_pipeline: 5000000, goal_meetings: 10, planned_budget: 38000, team: "Navy",          objective: "Engage Naval Sea Systems Command and Marine Corps programs. Target shipbuilding and expeditionary warfare decision-makers." },
  { name: "SOF Week 2026",      start: "2026-05-18", end: "2026-05-21", location: "Tampa, FL",           status: "Complete",    goal_leads: 143, goal_pipeline: 8400000, goal_meetings: 14, planned_budget: 66000, team: "Defense",       objective: "Capture special operations community contacts. Priority: USSOCOM program offices and Tier 1 unit representatives." },
  { name: "AFCEA TechNet 2026", start: "2026-06-23", end: "2026-06-25", location: "Baltimore, MD",       status: "Prospecting", goal_leads: 60,  goal_pipeline: 3500000, goal_meetings: 8,  planned_budget: 28000, team: "Federal",       objective: "Engage Army Cyber Command and Signal Corps leadership. Focus on zero-trust architecture and network modernization." },
  { name: "Sea-Air-Space 2026", start: "2026-04-07", end: "2026-04-09", location: "National Harbor, MD", status: "Complete",    goal_leads: 100, goal_pipeline: 7000000, goal_meetings: 12, planned_budget: 48000, team: "Navy",          objective: "Target Naval Aviation and PEO Aviation contacts. Focus on unmanned systems and ISR platform programs." },
  { name: "DSEI 2026",          start: "2026-09-09", end: "2026-09-12", location: "London, UK",          status: "Prospecting", goal_leads: 50,  goal_pipeline: 4000000, goal_meetings: 8,  planned_budget: 72000, team: "International", objective: "Expand European defense relationships. Target UK MoD, NATO procurement, and allied nation program offices." },
];

const SAMPLE_OVERRIDES = [
  { event_name: "AUSA Annual 2026",   segment: "Defense",       booth_size: "20x20", influenced_pipeline: 0,       net_new_pipeline: 0,       meetings: 0  },
  { event_name: "WEST 2026",          segment: "Navy",          booth_size: "10x20", influenced_pipeline: 4200000,  net_new_pipeline: 1800000,  meetings: 9  },
  { event_name: "SOF Week 2026",      segment: "Defense",       booth_size: "20x30", influenced_pipeline: 7100000,  net_new_pipeline: 3400000,  meetings: 12 },
  { event_name: "AFCEA TechNet 2026", segment: "Federal",       booth_size: "10x10", influenced_pipeline: 0,       net_new_pipeline: 0,       meetings: 0  },
  { event_name: "Sea-Air-Space 2026", segment: "Navy",          booth_size: "20x20", influenced_pipeline: 5800000,  net_new_pipeline: 2100000,  meetings: 11 },
  { event_name: "DSEI 2026",          segment: "International", booth_size: "10x20", influenced_pipeline: 0,       net_new_pipeline: 0,       meetings: 0  },
];

const SAMPLE_STAFF = [
  { name: "Sarah Chen",    email: "sarah.chen@sample.io",    role: "Booth Manager",     team: "Defense",       external: false },
  { name: "Mike Davis",    email: "mike.davis@sample.io",    role: "Lead Scanner",      team: "Navy",          external: false },
  { name: "Jordan Lee",    email: "jordan.lee@sample.io",    role: "Lead Scanner",      team: "Defense",       external: false },
  { name: "Alex Kim",      email: "alex.kim@sample.io",      role: "Meeting Host",      team: "Federal",       external: false },
  { name: "Taylor Morgan", email: "taylor.morgan@sample.io", role: "Technical SME",     team: "International", external: true  },
  { name: "Casey Rivera",  email: "casey.rivera@sample.io",  role: "Demo Specialist",   team: "Defense",       external: true  },
];

const SAMPLE_TARGETS = [
  { company: "Lockheed Martin",    contact_name: "James Morrison",  contact_title: "VP Programs",          opportunity_value: 2100000, opportunity_name: "C5ISR Modernization", status: "targeted" },
  { company: "Raytheon",           contact_name: "Lisa Johnson",    contact_title: "Director Procurement", opportunity_value: 1850000, opportunity_name: "EW Systems Upgrade",   status: "targeted" },
  { company: "Northrop Grumman",   contact_name: "David Williams",  contact_title: "Program Manager",      opportunity_value: 3200000, opportunity_name: "Autonomous Systems",   status: "targeted" },
  { company: "DARPA",              contact_name: "Sarah Liu",       contact_title: "Program Director",     opportunity_value: 4500000, opportunity_name: "AI Integration Phase 2", status: "targeted" },
  { company: "Palantir",           contact_name: "Robert Chen",     contact_title: "CTO",                  opportunity_value: 1200000, opportunity_name: "Data Platform Expansion", status: "targeted" },
  { company: "Booz Allen Hamilton",contact_name: "Karen Davis",     contact_title: "VP Federal",           opportunity_value: 2800000, opportunity_name: "Analytics Modernization", status: "targeted" },
];

const SAMPLE_MEETINGS = [
  { contact_name: "James Morrison",  company: "Lockheed Martin",    contact_title: "VP Programs",          meeting_type: "In-Person", status: "Completed", notes: "Strong interest in C5ISR integration. Budget confirmed for Q3. Follow up with proposal by EOW." },
  { contact_name: "Sarah Liu",       company: "DARPA",              contact_title: "Program Director",     meeting_type: "In-Person", status: "Completed", notes: "Discussed AI integration roadmap. Wants pilot program proposal. High priority lead." },
  { contact_name: "Robert Chen",     company: "Palantir",           contact_title: "CTO",                  meeting_type: "In-Person", status: "Completed", notes: "Technical deep dive on data platform. Agreed to 30-day POC. Budget approved." },
  { contact_name: "Lisa Johnson",    company: "Raytheon",           contact_title: "Director Procurement", meeting_type: "In-Person", status: "Completed", notes: "RFP expected Q4. Positioning well against incumbent. Need to get on approved vendor list." },
  { contact_name: "David Williams",  company: "Northrop Grumman",   contact_title: "Program Manager",      meeting_type: "In-Person", status: "Completed", notes: "Autonomous systems program expanding. Timeline 18 months. Strong fit with our capabilities." },
];

const DEBRIEF_TEXT = `## SOF Week 2026 — Post-Event Analysis

**Overall Grade: B+**

SOF Week 2026 delivered strong results across most metrics, with lead volume hitting 98% of goal and pipeline generation exceeding expectations by 12%.

---

### What Worked

**Lead capture efficiency was exceptional.** The team scanned 140 of 143 goal leads — a 97.9% attainment rate. Badge scanning via the ExIQ mobile app reduced per-lead capture time from 4.2 minutes to under 90 seconds compared to AUSA 2025.

**Meeting quality outperformed quantity.** 12 of 14 goal meetings were held, and 9 of those (75%) resulted in immediate follow-up commitments. The pre-scheduled meeting format introduced in the Brief stage directly contributed to this outcome.

**Pipeline influence exceeded goal.** $7.1M in influenced pipeline against an $8.4M target represents 84.5% attainment — the strongest pipeline performance since Sea-Air-Space 2024.

---

### What Fell Short

**2 meetings cancelled day-of.** Both were with USSOCOM contacts who were pulled into unscheduled sessions. Root cause: meeting zone was too close to the main expo floor and subject to interruption.

**Lead quality scoring skewed warm.** 42% of leads scored "warm" rather than "hot" despite strong engagement signals. Review lead qualification weighting — current model may undervalue title seniority for SOF-specific roles.

---

### Recommendations for SOF Week 2027

1. **Book meeting zone in a private area away from main floor.** Reduces cancellations and improves conversation quality.
2. **Add USSOCOM program office contact list to pre-event targets.** 6 unplanned high-value contacts were identified on the floor — they should have been targeted in advance.
3. **Recalibrate lead scoring for SOF community titles.** "Program Analyst" at USSOCOM carries more authority than the model currently weights.
4. **Increase booth size to 30x40.** Current 20x30 footprint created congestion during peak hours on Day 2.`;

const SAMPLE_REPS      = ["Sarah Chen","Mike Davis","Jordan Lee","Alex Kim"];
const SAMPLE_COMPANIES = ["Lockheed Martin","Raytheon","Boeing","Northrop Grumman","L3 Technologies","BAE Systems","Leidos","SAIC","Booz Allen","MITRE","General Dynamics","CACI","ManTech","Science Apps","AT&T Federal","AWS GovCloud","Microsoft Azure","Oracle Federal","Palantir","Anduril"];
const SAMPLE_FIRST     = ["James","Maria","Tom","Lisa","David","Anna","Robert","Karen","Paul","Susan"];
const SAMPLE_LAST      = ["Carter","Rodriguez","Williams","Johnson","Brown","Smith","Jones","Davis","Miller","Wilson"];
const SAMPLE_TITLES    = ["VP Engineering","Director Programs","Systems Architect","Program Manager","CTO","VP Sales","Director IT","Program Lead","Principal","Researcher"];

// Intelligence seed messages for tour
export const TOUR_INTEL_MESSAGES = [
  {
    role: "user",
    content: "Compare lead attainment across all 6 sample events and tell me which performed best relative to goals.",
  },
  {
    role: "assistant",
    content: `Based on the 6 sample events loaded in your account, here's how lead attainment stacked up:

**Top performers by attainment %:**

| Event | Goal | Actual | Attainment |
|-------|------|--------|-----------|
| WEST 2026 | 80 | 75 | 94% |
| Sea-Air-Space 2026 | 100 | 91 | 91% |
| SOF Week 2026 | 143 | 140 | 98% ← Best |

**Still ahead:**
- AUSA Annual 2026, AFCEA TechNet 2026, and DSEI 2026 are upcoming — goals set, no leads captured yet.

**Key insight:** SOF Week 2026 had the highest raw lead count (140) AND the highest attainment rate (98%). The combination of pre-event targeting, mobile badge scanning, and a 20x30 booth footprint appears to be the most effective format in your program.

**Recommendation:** Use SOF Week's setup as the template for AUSA Annual 2026. Pre-schedule 14+ meetings, target USSOCOM and Army program office contacts in advance, and brief the full team the day before.

Would you like me to build a comparison chart showing pipeline per lead across these events?`,
  },
];

// ── Data load / clear functions ────────────────────────────────────────────────
async function loadSampleData(orgId) {
  if (!orgId) { console.error("[Tour] orgId missing"); return; }

  // ── GROUP 1: Sequential — delete stale data, then insert foundations ────────
  // Delete all stale sample data in parallel first
  await Promise.allSettled([
    supabase.from("leads").delete().eq("org_id", orgId).eq("is_sample", true),
    supabase.from("events_calendar").delete().eq("org_id", orgId).eq("is_sample", true),
    supabase.from("staff_roster").delete().eq("org_id", orgId).like("email", "%sample.io%"),
    supabase.from("uploaded_files").delete().eq("org_id", orgId).eq("file_name", "sample_data"),
    ...SAMPLE_OVERRIDES.map(ov =>
      supabase.from("event_overrides").delete().eq("org_id", orgId).eq("event_name", ov.event_name)
    ),
    ...["WEST 2026","SOF Week 2026","Sea-Air-Space 2026"].map(n =>
      supabase.from("event_meetings").delete().eq("org_id", orgId).eq("event_name", n)
    ),
    supabase.from("event_briefs").delete().eq("org_id", orgId).eq("event_name", "SOF Week 2026"),
    supabase.from("event_shifts").delete().eq("org_id", orgId).eq("event_name", "SOF Week 2026"),
  ]);

  // Insert foundations — must be sequential to get back IDs
  const [calResult, staffResult] = await Promise.all([
    supabase.from("events_calendar").insert(
      SAMPLE_EVENTS_META.map(e => ({
        org_id: orgId, name: e.name, start_date: e.start, end_date: e.end,
        location: e.location, status: e.status, goal_leads: e.goal_leads,
        goal_pipeline: e.goal_pipeline, goal_meetings: e.goal_meetings,
        planned_budget: e.planned_budget, team: e.team, objective: e.objective, is_sample: true,
      }))
    ).select("id, name"),
    supabase.from("staff_roster").insert(
      SAMPLE_STAFF.map(s => ({ org_id: orgId, ...s }))
    ).select("id, name"),
  ]);

  if (calResult.error)   console.error("[Tour] events insert error:", calResult.error.message);
  if (staffResult.error) console.error("[Tour] staff insert error:", staffResult.error.message);

  const insertedEvents = calResult.data || [];
  const insertedStaff  = staffResult.data || [];

  // Build lookup maps
  const eventIdMap = {};
  insertedEvents.forEach(e => { eventIdMap[e.name] = e.id; });
  const staffIdMap = {};
  insertedStaff.forEach(s => { staffIdMap[s.name] = s.id; });
  const allStaffIds   = Object.values(staffIdMap);
  const sofCalEventId = eventIdMap["SOF Week 2026"];
  const sofId         = sofCalEventId;

  // ── GROUP 2: Parallel — everything that depends on event/staff IDs ──────────
  const leads = [];
  for (let i = 0; i < 150; i++) {
    const ev = SAMPLE_EVENTS_META[i % SAMPLE_EVENTS_META.length];
    const co = SAMPLE_COMPANIES[i % SAMPLE_COMPANIES.length];
    leads.push({
      org_id: orgId, primary_campaign_source: ev.name,
      first_name: SAMPLE_FIRST[i % 10], last_name: SAMPLE_LAST[i % 10],
      email: `contact${i}@${co.toLowerCase().replace(/[^a-z0-9]/g,"")}.com`,
      company: co, job_title: SAMPLE_TITLES[i % SAMPLE_TITLES.length],
      sales_executive: SAMPLE_REPS[i % SAMPLE_REPS.length],
      submission_date: ev.start, ranking: ["hot","warm","cold"][i % 3],
      source_file: "sample_data", is_sample: true,
      custom_data: { ranking: ["hot","warm","cold"][i % 3] },
    });
  }

  const SHIFTS = [
    { scheduled_date: "2026-05-18", start_time: "08:00", end_time: "17:00", zone_name: "Main Booth",     role: "Lead Scanner",    staff_names: ["Sarah Chen","Mike Davis"],    staff_ids: ["Sarah Chen","Mike Davis"].map(n=>staffIdMap[n]).filter(Boolean) },
    { scheduled_date: "2026-05-18", start_time: "10:00", end_time: "16:00", zone_name: "Meeting Zone A", role: "Meeting Host",    staff_names: ["Jordan Lee"],                 staff_ids: ["Jordan Lee"].map(n=>staffIdMap[n]).filter(Boolean) },
    { scheduled_date: "2026-05-19", start_time: "08:00", end_time: "17:00", zone_name: "Main Booth",     role: "Lead Scanner",    staff_names: ["Sarah Chen","Taylor Morgan"],  staff_ids: ["Sarah Chen","Taylor Morgan"].map(n=>staffIdMap[n]).filter(Boolean) },
    { scheduled_date: "2026-05-19", start_time: "10:00", end_time: "16:00", zone_name: "Meeting Zone A", role: "Meeting Host",    staff_names: ["Jordan Lee","Alex Kim"],       staff_ids: ["Jordan Lee","Alex Kim"].map(n=>staffIdMap[n]).filter(Boolean) },
    { scheduled_date: "2026-05-20", start_time: "08:00", end_time: "14:00", zone_name: "Main Booth",     role: "Lead Scanner",    staff_names: ["Mike Davis","Casey Rivera"],   staff_ids: ["Mike Davis","Casey Rivera"].map(n=>staffIdMap[n]).filter(Boolean) },
    { scheduled_date: "2026-05-20", start_time: "09:00", end_time: "13:00", zone_name: "Demo Area",      role: "Demo Specialist", staff_names: ["Taylor Morgan"],               staff_ids: ["Taylor Morgan"].map(n=>staffIdMap[n]).filter(Boolean) },
  ];

  await Promise.allSettled([
    // Leads — 3 batches in parallel
    supabase.from("leads").insert(leads.slice(0,50)),
    supabase.from("leads").insert(leads.slice(50,100)),
    supabase.from("leads").insert(leads.slice(100,150)),
    // Event overrides
    supabase.from("event_overrides").insert(
      SAMPLE_OVERRIDES.map(ov => ({
        org_id: orgId, event_name: ov.event_name, segment: ov.segment,
        booth_size: ov.booth_size, influenced_pipeline: ov.influenced_pipeline,
        net_new_pipeline: ov.net_new_pipeline,
        total_influenced_pipeline: ov.influenced_pipeline, meetings: ov.meetings,
      }))
    ),
    // Uploaded files
    supabase.from("uploaded_files").insert({ org_id: orgId, file_name: "sample_data", row_count: 150 }),
    // Pre-event targets
    sofId ? supabase.from("pre_event_targets").insert(
      SAMPLE_TARGETS.map(t => ({ org_id: orgId, event_id: sofId, source: "manual", ...t }))
    ) : Promise.resolve(),
    // Meetings for completed events
    supabase.from("event_meetings").insert([
      ...SAMPLE_MEETINGS.slice(0,2).map(m => ({ org_id: orgId, event_name: "WEST 2026", source: "execute", scheduled_date: "2026-02-17", status: "Completed", ...m })),
      ...SAMPLE_MEETINGS.slice(0,5).map(m => ({ org_id: orgId, event_name: "SOF Week 2026", source: "execute", scheduled_date: "2026-05-18", status: "Completed", ...m })),
      ...SAMPLE_MEETINGS.slice(0,2).map(m => ({ org_id: orgId, event_name: "Sea-Air-Space 2026", source: "execute", scheduled_date: "2026-04-07", status: "Completed", ...m })),
    ]),
  ]);

  // ── GROUP 3: Parallel — SOF Week enrichment (depends on IDs from Group 1) ──
  if (sofCalEventId) {
    await Promise.allSettled([
      // Brief
      supabase.from("event_briefs").insert({
        org_id: orgId, event_name: "SOF Week 2026", brief_type: "brief", status: "ready",
        booth_goals: "Generate 143 qualified leads from USSOCOM, Army Special Forces, and Tier 1 contractor community. Book 14 on-site meetings with program decision-makers.",
        staff_assigned: ["Sarah Chen","Mike Davis","Jordan Lee","Taylor Morgan"],
        scheduled_meetings: 14,
        competitors_attending: ["Palantir Technologies","Anduril Industries","CACI International","Leidos"],
        competitive_strategy: "Position against Palantir on ease of deployment and cost. Against Anduril on enterprise integration. Lead with DoD FedRAMP authorization and existing Army program office relationships.",
        differentiation_points: ["Only platform purpose-built for event exhibitors","FedRAMP authorized","Real-time badge scanning without vendor dependency","6-week implementation vs 6-month for competitors"],
        ai_narrative: "SOF Week 2026 is your highest-priority event this season. USSOCOM has an active procurement cycle and two of your existing customers are presenting on Day 1.",
        ai_talking_points: ["Ask about their current badge scanning workflow","Reference the 72-hour lead follow-up study showing 3x higher conversion","Lead with the offline-first mobile app","Have the ROI calculator ready"],
      }),
      // Debrief
      supabase.from("event_briefs").insert({
        org_id: orgId, event_name: "SOF Week 2026", brief_type: "debrief", status: "ready",
        ai_debrief_narrative: DEBRIEF_TEXT, ai_grade: "B+",
        ai_grade_rationale: "Strong lead volume and pipeline performance with minor shortfalls on meetings and lead quality scoring.",
        actual_leads: 140, actual_hot: 51, actual_meetings: 12, actual_pipeline: 7100000, goal_achievement_pct: 97.9,
        what_worked: "Badge scanning efficiency, meeting pre-scheduling, booth placement near USSOCOM pavilion.",
        what_didnt: "2 day-of meeting cancellations due to floor congestion.",
        recommendations_next_time: "Move meeting zone off main floor. Add USSOCOM contacts to pre-event targets.",
      }),
      // Meeting zones + event_roster on calendar event
      supabase.from("events_calendar").update({
        event_roster: allStaffIds,
        meeting_zones: [
          { id: "zone-a", name: "Meeting Zone A", capacity: 4, location: "Booth north corner, behind demo screen" },
          { id: "zone-b", name: "Executive Suite", capacity: 2, location: "Private room, Building B Level 2" },
        ]
      }).eq("id", sofCalEventId),
      // Shifts
      supabase.from("event_shifts").insert(
        SHIFTS.map(s => ({ org_id: orgId, event_name: "SOF Week 2026", source: "playbook", staff_emails: [], ...s }))
      ),
      // Badge integration
      supabase.from("badge_integrations").insert({
        org_id: orgId, event_id: sofCalEventId, provider: "cvent", badge_format: "qr",
        region: "na", cvent_event_id: "CVT-2026-SOFWK-001", cvent_exhibitor_id: "EXH-EXIQ-447",
        api_key: "demo_key_••••••••••••••••", is_active: true,
        test_status: "connected", last_tested_at: new Date().toISOString(),
      }),
    ]);
  }
}


async function clearSampleData(orgId) {
  if (!orgId) { console.error("[Tour] clearSampleData: orgId missing"); return; }

  const SAMPLE_EVENT_NAMES = [
    "AUSA Annual 2026", "WEST 2026", "SOF Week 2026",
    "AFCEA TechNet 2026", "Sea-Air-Space 2026", "DSEI 2026"
  ];

  // Step 1: Get sample event IDs before deleting anything
  const { data: sampleEvents } = await supabase
    .from("events_calendar").select("id, name")
    .eq("org_id", orgId).eq("is_sample", true);
  const sampleIds   = (sampleEvents || []).map(e => e.id);
  const sampleIdMap = {};
  (sampleEvents || []).forEach(e => { sampleIdMap[e.name] = e.id; });
  console.log("[Tour] Clearing", sampleIds.length, "sample events for org", orgId);

  // Step 2: Delete all dependent data in parallel
  // Safe to parallelize — no FK dependencies between these tables
  await Promise.allSettled([
    // Leads (two passes — by flag and by source_file)
    supabase.from("leads").delete().eq("org_id", orgId).eq("is_sample", true),
    supabase.from("leads").delete().eq("org_id", orgId).eq("source_file", "sample_data"),
    // Pre-event targets — all sample event IDs in one .in() call
    sampleIds.length > 0
      ? supabase.from("pre_event_targets").delete().eq("org_id", orgId).in("event_id", sampleIds)
      : Promise.resolve(),
    // Event-name-keyed tables — one call per table using .in() instead of looping
    supabase.from("event_overrides").delete().eq("org_id", orgId).in("event_name", SAMPLE_EVENT_NAMES),
    supabase.from("event_meetings").delete().eq("org_id", orgId).in("event_name", SAMPLE_EVENT_NAMES),
    supabase.from("event_briefs").delete().eq("org_id", orgId).in("event_name", SAMPLE_EVENT_NAMES),
    supabase.from("event_shifts").delete().eq("org_id", orgId).in("event_name", SAMPLE_EVENT_NAMES),
    // Staff roster
    supabase.from("staff_roster").delete().eq("org_id", orgId).like("email", "%sample.io%"),
    // Uploaded files
    supabase.from("uploaded_files").delete().eq("org_id", orgId).eq("file_name", "sample_data"),
    // Badge integrations — delete by event_id using IDs we already have
    sampleIds.length > 0
      ? supabase.from("badge_integrations").delete().eq("org_id", orgId).in("event_id", sampleIds)
      : Promise.resolve(),
  ]);

  // Step 3: Delete calendar events last — must be after all dependents
  const { error: eCal } = await supabase.from("events_calendar").delete().eq("org_id", orgId).eq("is_sample", true);
  if (eCal) console.error("[Tour] events_calendar delete error:", eCal.message);
  else console.log("[Tour] Sample data cleared successfully");
}

// ── Tour state ─────────────────────────────────────────────────────────────────
const TOUR_KEY_ACTIVE = "exiq_tour_active";
const TOUR_KEY_STEP   = "exiq_tour_step";
function saveTourState(step) {
  try { sessionStorage.setItem(TOUR_KEY_ACTIVE, "1"); sessionStorage.setItem(TOUR_KEY_STEP, String(step)); } catch {}
}
function loadTourState() {
  try {
    const active = sessionStorage.getItem(TOUR_KEY_ACTIVE);
    const step   = parseInt(sessionStorage.getItem(TOUR_KEY_STEP) || "0");
    return active === "1" ? step : null;
  } catch { return null; }
}
function clearTourState() {
  try {
    sessionStorage.removeItem(TOUR_KEY_ACTIVE);
    sessionStorage.removeItem(TOUR_KEY_STEP);
    sessionStorage.removeItem("exiq_welcome_nav");
  } catch {}
}

// ── Tour steps ─────────────────────────────────────────────────────────────────
const TOUR_STEPS = [
  {
    id: "analytics", nav: "analytics", label: "Analytics",
    title: "Your event ROI — at a glance",
    bullets: [
      "150 leads across 6 events — filter by event, team, rep, or date range",
      "Pipeline by event shows $17M+ influenced across completed shows",
      "Scorecard snapshot grades each event A through F on goal attainment",
    ],
    callout: "Filter to SOF Week 2026 in the 'All Events' dropdown — it's the most complete event with targets, meetings, leads, and an AI Debrief already generated.",
  },
  {
    id: "playbook", nav: "playbook", label: "Playbook",
    title: "Every event, six stages",
    bullets: [
      "Click SOF Week 2026 — it has a full Playbook with data in every stage",
      "Discover: event objective set. Plan: goals, targets, and pipeline defined",
      "Analyze: AI Debrief already generated with grade, insights, and recommendations",
    ],
    callout: "Open SOF Week 2026 and click through Discover → Plan → Execute → Analyze. Each stage has sample data showing exactly what the full experience looks like.",
  },
  {
    id: "calendar", nav: "calendar", label: "Calendar",
    title: "Your full event program",
    bullets: [
      "6 events on the calendar — mix of Complete, Confirmed, and Prospecting",
      "Switch to Gantt view to see your season as a timeline",
      "Color-coded by status — see what's upcoming vs. completed at a glance",
    ],
    callout: "Click the Gantt tab, then set the zoom to Quarterly. You'll see your entire defense event program laid out on one timeline — past and future.",
  },
  {
    id: "intelligence", nav: "intelligence", label: "Intelligence",
    title: "AI that works before and after",
    bullets: [
      "A sample analysis has been pre-loaded — see how the AI interprets your event data",
      "Ask any question about your events — ROI, comparisons, recommendations",
      "Save charts to your dashboard or export analysis to share with your team",
    ],
    callout: "Scroll up to see the pre-loaded analysis comparing lead attainment across all 6 events. Then type a follow-up question to see the AI respond in real time.",
  },
  {
    id: "mobile", nav: null, label: "Mobile",
    title: "Scan badges at any event",
    bullets: [
      "iOS and Android app — scan QR codes and barcodes from any badge provider",
      "Qualification fields filled in on the show floor, right after scanning",
      "Works fully offline — leads sync when you reconnect to Wi-Fi",
    ],
    callout: "Scan the QR codes below to download the ExIQ app. Once installed, invite your team from Settings — Members and they're ready for your next event.",
    showQR: true,
  },
];

// ── Shared components ──────────────────────────────────────────────────────────
function Overlay({ children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9000,
      background: "rgba(6,16,24,0.88)", backdropFilter: "blur(5px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: FONT,
    }}>
      <div onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function Card({ children, maxWidth = 520 }) {
  return (
    <div style={{
      background: SURF, border: `1px solid ${BORD}`, borderRadius: 16,
      padding: "40px 36px", maxWidth, width: "100%",
      boxShadow: "0 32px 80px rgba(0,0,0,0.6)", position: "relative", fontFamily: FONT,
    }}>
      {children}
    </div>
  );
}

function PathCard({ icon, title, desc, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", gap: 16,
        background: hov ? `${CYAN}0A` : SURF2, border: `1px solid ${hov ? CYAN + "55" : BORD}`,
        borderRadius: 12, padding: "18px 20px", cursor: "pointer", marginBottom: 10,
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: `${CYAN}18`, border: `1px solid ${CYAN}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 700, color: CYAN }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12, color: SEC, lineHeight: 1.55 }}>{desc}</div>
      </div>
      <div style={{ color: CYAN, fontSize: 16, flexShrink: 0 }}>→</div>
    </div>
  );
}

function QRCode({ url, label, size = 88 }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}&bgcolor=0F2230&color=22D3EE&margin=2`;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <div style={{ padding: 8, background: SURF2, borderRadius: 10, border: `1px solid ${BORD}` }}>
        <img src={qrUrl} width={size} height={size} alt={label} style={{ display: "block", borderRadius: 4 }} />
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: MUTED }}>{label}</div>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────────
export function WelcomeScreen({ userName, orgName, userId, orgId, onDismiss, onImportEvents, onNewEvent, setView, reloadProfile }) {
  const resumeStep = loadTourState();
  const wasCleared = (() => { try { return sessionStorage.getItem("exiq_show_cleared") === "1"; } catch { return false; } })();
  if (wasCleared) { try { sessionStorage.removeItem("exiq_show_cleared"); } catch {} }

  const [mode,       setMode]       = useState(wasCleared ? "cleared" : resumeStep !== null ? "tour" : "home");
  const [tourStep,   setTourStep]   = useState(resumeStep ?? 0);
  const [loadingMsg, setLoadingMsg] = useState("Loading sample data...");
  const [collapsed,  setCollapsed]  = useState(false);

  // On tour resume: navigate and seed intelligence messages
  useEffect(() => {
    if (resumeStep !== null) {
      window.dispatchEvent(new CustomEvent("exiq_intel_seed", { detail: TOUR_INTEL_MESSAGES }));
      const s = TOUR_STEPS[resumeStep];
      if (s.nav) setTimeout(() => {
        window.dispatchEvent(new CustomEvent("exiq_nav", { detail: s.nav }));
      }, 150);
      // Auto-select SOF Week if resuming on playbook step
      if (s.id === "playbook") setTimeout(() => {
        window.dispatchEvent(new CustomEvent("exiq_select_event", { detail: "SOF Week 2026" }));
      }, 300);
    }
  }, []);

  const step   = TOUR_STEPS[tourStep];
  const isLast = tourStep === TOUR_STEPS.length - 1;

  async function markSeen() {
    try { await supabase.from("profiles").update({ has_seen_welcome: true }).eq("id", userId); } catch {}
    try { localStorage.setItem("exiq_welcome_dismissed", "1"); } catch {}
    // Refresh the in-memory profile so has_seen_welcome = true is reflected
    // immediately — prevents the tour from showing again on next render
    try { if (reloadProfile) await reloadProfile(); } catch {}
  }

  async function handleSkip() {
    clearTourState();
    await markSeen();
    onDismiss?.();
  }

  async function startTour() {
    setMode("loading");
    setLoadingMsg("Loading sample data...");
    try { if (orgId) await loadSampleData(orgId); } catch(e) { console.error("Sample data load failed:", e); }
    try { sessionStorage.setItem("exiq_welcome_nav", "analytics"); } catch {}
    saveTourState(0);
    window.location.reload();
  }

  function navToStep(idx) {
    const s = TOUR_STEPS[idx];
    if (s.nav) window.dispatchEvent(new CustomEvent("exiq_nav", { detail: s.nav }));
    // Auto-select SOF Week 2026 when entering playbook step
    if (s.id === "playbook") {
      try { sessionStorage.setItem("exiq_tour_playbook_event", "SOF Week 2026"); } catch {}
      window.dispatchEvent(new CustomEvent("exiq_select_event", { detail: "SOF Week 2026" }));
    }
    // Seed intelligence messages when navigating to intelligence step
    if (s.id === "intelligence") window.dispatchEvent(new CustomEvent("exiq_intel_seed", { detail: TOUR_INTEL_MESSAGES }));
    saveTourState(idx);
  }

  function handleNext() {
    if (isLast) { finishTour(); return; }
    const next = tourStep + 1;
    setTourStep(next);
    navToStep(next);
  }

  function handleBack() {
    const prev = tourStep - 1;
    setTourStep(prev);
    navToStep(prev);
  }

  async function finishTour() {
    clearTourState();
    window.dispatchEvent(new CustomEvent("exiq_intel_seed", { detail: [] }));
    setMode("loading");
    setLoadingMsg("Clearing sample data...");
    try { if (orgId) await clearSampleData(orgId); } catch(e) { console.error("Clear failed:", e); }
    // Reload so App re-fetches data fresh — show cleared screen after reload
    try { sessionStorage.setItem("exiq_show_cleared", "1"); } catch {}
    window.location.reload();
  }

  async function skipTour() {
    clearTourState();
    window.dispatchEvent(new CustomEvent("exiq_intel_seed", { detail: [] }));
    try { if (orgId) await clearSampleData(orgId); } catch {}
    try { sessionStorage.setItem("exiq_show_cleared", "1"); } catch {}
    window.location.reload();
  }

  // ── HOME ───────────────────────────────────────────────────────────────────
  if (mode === "home") return (
    <Overlay>
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
          <ExIQMark size={44} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: CYAN, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>Welcome to ExIQ</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: TEXT }}>{userName ? `Hi ${userName.split(" ")[0]}` : "Hi there"}</div>
          </div>
        </div>
        <div style={{ fontSize: 14, color: SEC, lineHeight: 1.65, marginBottom: 28 }}>
          {orgName ? `${orgName} is` : "Your account is"} ready. How would you like to start?
        </div>
        <PathCard icon="▶" title="Take a tour"
          desc="We'll load a full sample season — 6 defense events, 150 leads, meetings, targets, and an AI Debrief. Explore every feature with real data. About 3 minutes."
          onClick={startTour} />
        <PathCard icon="◈" title="Add my events"
          desc="Skip straight to adding your real events. Create one manually or upload your full calendar from a CSV."
          onClick={() => setMode("events")} />
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button onClick={handleSkip} style={{ background: "transparent", border: "none", color: MUTED, fontSize: 12, cursor: "pointer", fontFamily: FONT }}>
            Skip — I'll explore on my own
          </button>
        </div>
      </Card>
    </Overlay>
  );

  // ── LOADING ────────────────────────────────────────────────────────────────
  if (mode === "loading") return (
    <Overlay>
      <Card maxWidth={360}>
        <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
          <div style={{ display: "inline-block", marginBottom: 20 }}><ExIQMark size={48} /></div>
          <div style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 6 }}>{loadingMsg}</div>
          <div style={{ fontSize: 13, color: MUTED, marginBottom: 24 }}>Just a moment.</div>
          <div style={{ display: "flex", justifyContent: "center", gap: 7 }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: CYAN, opacity: 0.4, animation: `exiq-pulse 1.2s ease-in-out ${i*0.2}s infinite` }}/>
            ))}
          </div>
        </div>
        <style>{`@keyframes exiq-pulse{0%,100%{opacity:.25;transform:scale(.75)}50%{opacity:1;transform:scale(1)}}`}</style>
      </Card>
    </Overlay>
  );

  // ── TOUR — compact floating card, NO overlay ───────────────────────────────
  if (mode === "tour") return (
    <div style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
      zIndex: 9000, width: "min(600px, calc(100vw - 32px))",
      fontFamily: FONT, filter: "drop-shadow(0 8px 48px rgba(0,0,0,0.8))",
    }}>
      <div style={{ height: 2, borderRadius: "2px 2px 0 0", background: `linear-gradient(90deg, transparent, ${CYAN}, transparent)` }}/>
      <div style={{ background: BG, border: `1px solid ${CYAN}44`, borderTop: "none", borderRadius: "0 0 14px 14px", padding: "18px 22px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: collapsed ? 0 : 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {TOUR_STEPS.map((s, i) => (
              <button key={i} onClick={() => { setTourStep(i); navToStep(i); if(collapsed) setCollapsed(false); }} title={s.label}
                style={{ width: i === tourStep ? 24 : 7, height: 7, borderRadius: 4, border: "none", cursor: "pointer", padding: 0, transition: "all 0.25s",
                  background: i === tourStep ? CYAN : i < tourStep ? `${CYAN}55` : BORD }}
              />
            ))}
            <span style={{ fontSize: 11, color: MUTED, marginLeft: 4 }}>{tourStep + 1} / {TOUR_STEPS.length}</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => setCollapsed(v => !v)} style={{ background: "transparent", border: "none", color: MUTED, fontSize: 11, cursor: "pointer", fontFamily: FONT, padding: "2px 8px" }}>
              {collapsed ? "Show ▲" : "Hide ▼"}
            </button>
            <button onClick={skipTour} style={{ background: "transparent", border: "none", color: MUTED, fontSize: 11, cursor: "pointer", fontFamily: FONT, padding: "2px 6px" }}>
              End tour
            </button>
          </div>
        </div>

        {!collapsed && (
        <>
        <div style={{ fontSize: 16, fontWeight: 800, color: TEXT, marginBottom: 10, lineHeight: 1.3, letterSpacing: "-0.01em" }}>{step.title}</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 12 }}>
          {step.bullets.map((b, i) => (
            <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: CYAN, flexShrink: 0, marginTop: 7 }}/>
              <span style={{ fontSize: 13, color: SEC, lineHeight: 1.6 }}>{b}</span>
            </div>
          ))}
        </div>

        {step.showQR && (
          <div style={{ display: "flex", justifyContent: "center", gap: 32, padding: "12px 0 8px", marginBottom: 10 }}>
            <QRCode url="https://apps.apple.com/app/exiq/id000000000" label="App Store" size={88} />
            <QRCode url="https://play.google.com/store/apps/details?id=io.exiq.app" label="Google Play" size={88} />
          </div>
        )}

        <div style={{ background: `${CYAN}0C`, border: `1px solid ${CYAN}22`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ width: 18, height: 18, borderRadius: "50%", background: `${CYAN}22`, border: `1px solid ${CYAN}44`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: CYAN }}/>
          </div>
          <div style={{ fontSize: 12, color: SEC, lineHeight: 1.6 }}>{step.callout}</div>
        </div>
        </>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          {tourStep > 0 && (
            <button onClick={handleBack}
              style={{ background: "transparent", border: `1px solid ${BORD}`, borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: SEC, cursor: "pointer", fontFamily: FONT }}
              onMouseEnter={e => e.currentTarget.style.borderColor = CYAN + "55"}
              onMouseLeave={e => e.currentTarget.style.borderColor = BORD}
            >Back</button>
          )}
          <button onClick={handleNext}
            style={{ flex: 1, background: isLast ? `linear-gradient(135deg, ${CYAN}, #0891B2)` : CYAN, border: "none", borderRadius: 7, padding: "9px 0", fontSize: 13, fontWeight: 700, color: BG, cursor: "pointer", fontFamily: FONT, boxShadow: `0 0 20px ${CYAN}33`, letterSpacing: "0.01em" }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = `0 0 30px ${CYAN}55`}
            onMouseLeave={e => e.currentTarget.style.boxShadow = `0 0 20px ${CYAN}33`}
          >
            {isLast ? "Done — add my events" : `Next: ${TOUR_STEPS[tourStep + 1].label} →`}
          </button>
        </div>
      </div>
    </div>
  );

  // ── CLEARED ────────────────────────────────────────────────────────────────
  if (mode === "cleared") return (
    <Overlay>
      <Card maxWidth={500}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ display: "inline-block", marginBottom: 16 }}><ExIQMark size={48} /></div>
          <div style={{ width: 52, height: 52, borderRadius: "50%", margin: "0 auto 16px", background: `${CYAN}18`, border: `1px solid ${CYAN}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: CYAN }}>✓</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: CYAN, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>Tour complete</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: TEXT, marginBottom: 10, lineHeight: 1.3 }}>Sample data cleared.</div>
          <div style={{ fontSize: 14, color: SEC, lineHeight: 1.7 }}>
            All 6 sample events, 150 leads, meetings, targets, and the AI Debrief have been removed. Your account is clean and ready for your real data.
          </div>
        </div>
        <div style={{ background: `${CYAN}0A`, border: `1px solid ${CYAN}22`, borderRadius: 10, padding: "14px 16px", marginBottom: 22 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: CYAN, marginBottom: 4 }}>Next step</div>
          <div style={{ fontSize: 13, color: SEC, lineHeight: 1.6 }}>Add your events to the Playbook. Once events exist, you can upload leads, connect Salesforce or HubSpot, and start tracking real ROI.</div>
        </div>
        <PathCard icon="+" title="Add your first event"
          desc="Create a single event manually. Name, dates, location, and goals — you'll be in the Playbook in seconds."
          onClick={() => { onNewEvent?.(); handleSkip(); }} />
        <PathCard icon="↑" title="Import all your events"
          desc="Upload your full event calendar from a CSV. Download the template, fill it in, and all events are created at once."
          onClick={() => { onImportEvents?.(); handleSkip(); }} />
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button onClick={handleSkip} style={{ background: "transparent", border: "none", color: MUTED, fontSize: 12, cursor: "pointer", fontFamily: FONT }}>
            Skip for now
          </button>
        </div>
      </Card>
    </Overlay>
  );

  // ── EVENTS (direct path) ───────────────────────────────────────────────────
  if (mode === "events") return (
    <Overlay>
      <Card maxWidth={500}>
        <button onClick={() => setMode("home")} style={{ position: "absolute", top: 20, left: 24, background: "transparent", border: "none", color: MUTED, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>Back</button>
        <button onClick={handleSkip} style={{ position: "absolute", top: 20, right: 20, background: "transparent", border: "none", color: MUTED, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
        <div style={{ textAlign: "center", marginBottom: 28, paddingTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: CYAN, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>Add Events</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: TEXT, marginBottom: 10 }}>Events come first</div>
          <div style={{ fontSize: 13, color: SEC, lineHeight: 1.65 }}>Once your events are in the Playbook, you can upload lead data, connect your CRM, and track performance.</div>
        </div>
        <PathCard icon="+" title="Add your first event"
          desc="Create a single event manually with name, dates, and goals."
          onClick={() => { onNewEvent?.(); handleSkip(); }} />
        <PathCard icon="↑" title="Import all your events"
          desc="Upload your full event calendar from a CSV. Download the template, fill it in, and all events are created at once."
          onClick={() => { onImportEvents?.(); handleSkip(); }} />
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button onClick={handleSkip} style={{ background: "transparent", border: "none", color: MUTED, fontSize: 12, cursor: "pointer", fontFamily: FONT }}>Skip for now</button>
        </div>
      </Card>
    </Overlay>
  );

  return null;
}
